// src/components/SubmitTime/SubmitTime.tsx
// Main SubmitTime component - Time tracking and reply submission
// Migrated from legacy SubmitTime.js to React 19 with TypeScript

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { conversationBus } from '@/lib/conversationBus';
import { validateYesterdayMetrics } from '@/hooks/useYesterdayMetricsValidation';
import { useSubmitTimeForm } from './hooks';
import {
  getOvertimeMessage,
  getWarningRecipients,
  buildWarningBody,
  buildWarningTagsHtml,
  type WarningRecipient,
} from './utils/overtimeWarning';
import {
  TimeInputFields,
  SelectorsSection,
  DynamicCustomFields,
  ReplyEditor,
  SubmitTimeModal,
} from './components';
import type {
  SubmitTimeProps,
  LayerItem,
  PhaseItem,
  ProjectItem,
  TypeItem,
  TaskItem,
  Team,
  TimeTrackingItem,
  ReplyRequest,
  DeleteAndUpdateRequest,
  DeleteReplyRequest,
  TagPerson,
  CustomFieldResponse,
  SubmitTimeConfiguration,
} from './types';
import './SubmitTime.css';

/** Extract person tags from HTML body */
function getTagsInBody(body: string): TagPerson[]
{
  if (!body) return [];
  try
  {
    const parser = new DOMParser();
    const doc = parser.parseFromString(body, 'text/html');
    const spans = doc.querySelectorAll('span[person-id]');
    return Array.from(spans).map(span => ({
      PersonId: parseInt(span.getAttribute('person-id') || '0'),
      ConversationId: 0,
      ReplyId: 0,
      ReplyFollowing: 0,
    }));
  }
  catch
  {
    return [];
  }
}

/** Format date as MM/DD/YYYY */
function formatDate(dateStr: string): string
{
  const d = new Date(dateStr);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getUTCFullYear()}`;
}

export const SubmitTime: React.FC<SubmitTimeProps> = props =>
{
  const {
    api,
    title = 'Submit Time',
    typesubmittime,
    conversation,
    openmodal,
    phaseselected: _phaseselected,
    objedit,
    socketconnectionid,
    onClose,
    onclose,
    onSave,
    onSubmitTime,
    onaddreply,
    onaddreplywarning,
    onupdatereply,
  } = props;

  // Refs to always hold the latest callback values — avoids stale closures in async handlers
  const onSubmitTimeRef = useRef(onSubmitTime);
  onSubmitTimeRef.current = onSubmitTime;
  const onaddreplyRef = useRef(onaddreply);
  onaddreplyRef.current = onaddreply;

  const isSubmittingRef = useRef(false);

  // Initialize form with master hook
  const form = useSubmitTimeForm(props);
  const {
    hours,
    date,
    estimatedField,
    remainingField,
    actualField,
    remainingId,
    actualId,
    description,
    body,
    attachments,
    isEdit,
    setBtnSubmit,
    isOpenQuestionDelete,
    setIsOpenQuestionDelete,
    timeCalculations,
    validation,
    customFields,
    timeTracking,
    cascadeLoading,
    hoursFormat,
    handleHoursChange,
    handleHoursBlur,
    handleDateChange,
    handleEstimatedChange,
    handleRemainingChange,
    handleDescriptionChange,
    handleBodyChange,
    handleAttachmentAdd,
    handleAttachmentRemove,
    resetForm,
    validateForm,
  } = form;

  // Local state for modal control
  // customKey remounts the child fields (Summernote included) — bumped on "Save and New"
  const [customKey, setCustomKey] = useState(() => Math.floor(Math.random() * 1000000));
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Cache for GetCustomFieldsByItemId + GetSubmitTimeConfigurations responses
  const [cachedFieldsResponse, setCachedFieldsResponse] = useState<CustomFieldResponse[]>([]);
  const [cachedConfigurations, setCachedConfigurations] = useState<SubmitTimeConfiguration[]>([]);

  // "Save and New": keeps the modal open and re-initializes it after a successful save
  const saveAndNewRef = useRef(false);
  const [reopenNonce, setReopenNonce] = useState(0);

  // Handle modal close
  const handleClose = useCallback(() =>
  {
    isSubmittingRef.current = false;
    if (onClose)
    {
      onClose();
    }
    else if (onclose)
    {
      onclose();
    }
    setCachedFieldsResponse([]);
    setCachedConfigurations([]);
    resetForm(false);
  }, [onClose, onclose, resetForm]);

  // Apply cached custom field data for a selected layer (no extra API call)
  const applyLayerData = useCallback(
    (
      layer: LayerItem,
      fieldsResponse: CustomFieldResponse[],
      configurations: SubmitTimeConfiguration[],
    ) =>
    {
      customFields.setBackupCustomFields([...fieldsResponse] as any);

      // Derive configValueId: for type 1 use conversation, for type 2 use selected type
      const configValueId =
        (typesubmittime === 1
          ? conversation?.ConfigValueId
          : cascadeLoading.typeSelected?.ConfigValueID) ?? undefined;

      if (configurations.length > 0)
      {
        const processed = customFields.processCustomFieldResponse(
          fieldsResponse,
          configurations,
          layer.LayerId,
          configValueId,
          {
            hasPhases: !!conversation?.HasPhases,
            showPhases: false,
            typesubmittime,
          },
        );
        customFields.setAddedConfigurations(processed);
      }
      else
      {
        customFields.setAddedConfigurations([]);
      }

      const estRem = customFields.getEstAndRem(
        fieldsResponse,
        layer.LayerId,
        'Estimated',
        'Remaining',
        'Actual',
        'Phases',
      );
      form.setEstimatedField(estRem.estimated);
      if (estRem.estimated)
      {
        validation.updateValidation('estimated', false);
      }
      // Keep entered hours applied on top of the new layer's Remaining/Actual
      form.applyTimeFieldBaselines(estRem);
    },
    [customFields, form, typesubmittime, conversation, cascadeLoading, validation],
  );

  // Load layers for typesubmittime === 1 (conversation mode).
  // Layers come directly from conversation.ConversationLayers (already available in props).
  // Custom fields are pre-loaded via GetCustomFieldsByItemId + GetSubmitTimeConfigurations
  // if CoreTaskId is available, so that selecting a layer requires no extra API call.
  const loadLayersForConversation = useCallback(async () =>
  {
    const convLayers = conversation?.ConversationLayers;

    if (convLayers && convLayers.length > 0)
    {
      const layers: LayerItem[] = convLayers.map(cl => ({
        LayerId: cl.LayerId,
        Name: cl.LayerName || (cl as any).Name || '',
        Color: cl.LayerColor || (cl as any).Color,
      }));
      cascadeLoading.setDataLayers(layers);

      // Pre-load custom fields so layer selection needs no extra API call
      const coreTaskId = conversation?.CoreTaskId || conversation?.CoreTaskID;
      const projectId = conversation?.ProjectId || (conversation as any)?.ProjectID;

      // Resolve which layer to auto-select: prefer C2_layerid from localStorage, else first
      const storedLayerId = parseInt(localStorage.getItem('C2_layerid') || '0', 10);
      const autoLayer: LayerItem | null =
        (storedLayerId > 0 ? layers.find(l => l.LayerId === storedLayerId) : undefined) ||
        layers[0] ||
        null;

      if (coreTaskId && projectId)
      {
        try
        {
          const { submitTimeService } = await import('@/services/submitTimeService');
          const [configurations, fieldsResponse] = await Promise.all([
            submitTimeService.getSubmitTimeConfigurations(),
            submitTimeService.getCustomFieldsByItemId(coreTaskId, false, projectId),
          ]);
          setCachedConfigurations(configurations);
          setCachedFieldsResponse(fieldsResponse);

          if (autoLayer)
          {
            cascadeLoading.setSelectedId(autoLayer);
            applyLayerData(autoLayer, fieldsResponse, configurations);
          }
        }
        catch (error)
        {
          console.error('Error pre-loading custom fields:', error);
          cascadeLoading.setSelectedId(autoLayer);
        }
      }
      else
      {
        // No CoreTaskId available — still auto-select
        cascadeLoading.setSelectedId(autoLayer);
      }
    }
    else
    {
      // Fallback: conversation has no ConversationLayers — load all person layers
      const layers = await cascadeLoading.loadLayers(api, conversation?.ConversationId);
      if (layers.length > 0)
      {
        const storedLayerId = parseInt(localStorage.getItem('C2_layerid') || '0', 10);
        const autoLayer: LayerItem | null =
          (storedLayerId > 0 ? layers.find(l => l.LayerId === storedLayerId) : undefined) ||
          layers[0] ||
          null;
        cascadeLoading.setSelectedId(autoLayer);
      }
    }
  }, [conversation, api, cascadeLoading, applyLayerData]);

  // Configuration method - determines what to load based on mode.
  // Returns a promise so initializeModal can await full data before hiding the skeleton.
  const configuration = useCallback(async () =>
  {
    if (isEdit && objedit)
    {
      await onEditMode();
      return;
    }

    validation.updateValidation('disabledPhases', false);

    if (typesubmittime === 1 && conversation?.HasPhases)
    {
      // Conversation with phases
      validation.updateValidation('showPhases', true);
      validation.updateValidation('validPhases', false);
      const autoPhase = await getPhases();
      // Pass the selected phase as override so loadLayers → getCustomFieldValues uses it
      // without relying on stale state from setPhaseSelected
      await loadLayers(autoPhase);
    }
    else if (typesubmittime === 2)
    {
      // Standalone project mode — load all dropdowns in parallel
      validation.updateValidation('showPhases', false);
      validation.updateValidation('showProjects', true);
      validation.updateValidation('showType', true);
      validation.updateValidation('showTask', true);

      const [layers] = await Promise.all([
        cascadeLoading.loadAllLayers(api),
        cascadeLoading.loadAllProjects(api),
        cascadeLoading.loadAllTypes(api),
      ]);

      // Auto-select default layer from localStorage
      const storedLayerId = parseInt(localStorage.getItem('C2_layerid') || '0', 10);
      if (storedLayerId > 0 && layers.length > 0)
      {
        const defaultLayer = layers.find(l => l.LayerId === storedLayerId);
        if (defaultLayer)
        {
          cascadeLoading.setSelectedId(defaultLayer);
        }
      }
    }
    else
    {
      // Conversation without phases — layers come from conversation.ConversationLayers
      validation.updateValidation('showPhases', false);
      await loadLayersForConversation();
    }
  }, [isEdit, objedit, typesubmittime, conversation, api, validation, cascadeLoading]);

  // Initialize modal: load teams first, then configure
  const initializeModal = useCallback(async () =>
  {
    setIsInitializing(true);
    try
    {
      const { submitTimeService } = await import('@/services/submitTimeService');
      const teamsResponse = await submitTimeService.getTeams();
      cascadeLoading.setDefaultTeams(teamsResponse.teams);
      if (teamsResponse.defaultteam)
      {
        cascadeLoading.setSelectedTeam(teamsResponse.defaultteam);
      }
      timeTracking.setTimeTrackingTeam(teamsResponse.submittimeteam);
      timeTracking.setMaxMinutesPerDay(teamsResponse.submittimeteam ? 250 * 60 : 16 * 60);
    }
    catch (error)
    {
      console.error('Error loading teams:', error);
    }

    // Await configuration so the skeleton stays until all initial data is ready
    try
    {
      await configuration();
    }
    catch (error)
    {
      console.error('Error during configuration:', error);
    }
    finally
    {
      setIsInitializing(false);
    }
  }, [cascadeLoading, timeTracking, configuration]);

  // Always points to the latest initializeModal — avoids stale closures in async handlers
  const initializeModalRef = useRef(initializeModal);
  initializeModalRef.current = initializeModal;

  // "Save and New": re-initialize after the form state has been reset and re-rendered,
  // so the modal ends up in the same state as a fresh open without ever closing.
  useEffect(() =>
  {
    if (reopenNonce === 0) return;
    initializeModalRef.current();
  }, [reopenNonce]);

  // Configuration on mount / modal open
  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    if (openmodal)
    {
      initializeModal();
    }
  }, [openmodal]);

  // Update modal state when prop changes
  useEffect(() =>
  {
    form.setOpenmodal(openmodal);
  }, [openmodal]);

  // Edit mode setup
  const onEditMode = useCallback(async () =>
  {
    if (!objedit) return;

    validation.setValForm({
      ...validation.valForm,
      disabledLayers: true,
      showPhases: false,
      validPhases: false,
      disabledPhases: true,
      showProjects: true,
      validProject: false,
      disabledProject: true,
      showType: true,
      validType: false,
      disabledType: true,
      showTask: true,
      validTask: false,
      disabledTask: true,
    });

    // Load all layers and auto-select the one matching objedit.LayerId
    const convertedDate = new Date(objedit.date);
    const layers = await cascadeLoading.loadAllLayers(
      api,
      objedit.LayerId,
      convertedDate.toISOString(),
    );
    const matchingLayer = layers.find(l => l.LayerId === objedit.LayerId);
    if (matchingLayer)
    {
      cascadeLoading.setSelectedId(matchingLayer);
    }

    // Populate options arrays with the single selected item so the disabled
    // comboboxes can find their value (react-select requires the option to exist in the list)
    cascadeLoading.setDataProjects([
      {
        ProjectId: objedit.ProjectID,
        ProjectName: objedit.ProjectName,
        Name: objedit.ProjectName,
        ConfigValueId: objedit.ConfigValueID,
      },
    ]);
    cascadeLoading.setDataType([
      {
        ConfigValueID: objedit.ConfigValueID,
        Text: objedit.SideMenuText,
      },
    ]);
    cascadeLoading.setDataTasks([
      {
        ConversationId: objedit.ConversationId,
        CoreTaskID: objedit.CoreTaskID,
        Name: objedit.title,
      },
    ]);

    cascadeLoading.setProjectSelected({
      ProjectId: objedit.ProjectID,
      ProjectName: objedit.ProjectName,
      Name: objedit.ProjectName,
      ConfigValueId: objedit.ConfigValueID,
    });

    cascadeLoading.setTypeSelected({
      ConfigValueID: objedit.ConfigValueID,
      Text: objedit.SideMenuText,
    });

    cascadeLoading.setTaskSelected({
      ConversationId: objedit.ConversationId,
      CoreTaskID: objedit.CoreTaskID,
      Name: objedit.title,
    });

    // Time posted to a phase is stored with the phase's CoreTaskId — show that phase read-only
    if (objedit.ConversationId)
    {
      const phases = await cascadeLoading.loadPhases(api, objedit.ConversationId);
      const postedPhase = phases.find(
        p => (p.CoreTaskId || p.CoreTaskID) === objedit.CoreTaskID,
      );
      if (postedPhase)
      {
        cascadeLoading.setPhaseSelected(postedPhase);
        validation.updateValidation('showPhases', true);
      }
    }

    handleHoursChange(objedit.Hours);
    form.setHoursInitial(objedit.Hours);
    handleDateChange(objedit.date);
    handleBodyChange(objedit.Description);
    form.setAttachments(objedit.attachments || []);

    if (objedit.TeamId)
    {
      const team = cascadeLoading.defaultTeams.find(t => t.TeamId === objedit.TeamId);
      if (team)
      {
        cascadeLoading.setSelectedTeam(team);
      }
    }

    // Load custom fields to populate Remaining and Actual (GetSubmitTimeConfigurations + GetCustomFieldsByItemId)
    if (matchingLayer && objedit.CoreTaskID && objedit.ProjectID)
    {
      try
      {
        const { submitTimeService } = await import('@/services/submitTimeService');
        const [configurations, fieldsResponse] = await Promise.all([
          submitTimeService.getSubmitTimeConfigurations(),
          submitTimeService.getCustomFieldsByItemId(objedit.CoreTaskID, false, objedit.ProjectID),
        ]);

        setCachedConfigurations(configurations);
        setCachedFieldsResponse(fieldsResponse);
        customFields.setBackupCustomFields([...fieldsResponse] as any);

        if (configurations.length > 0)
        {
          const processed = customFields.processCustomFieldResponse(
            fieldsResponse,
            configurations,
            matchingLayer.LayerId,
            objedit.ConfigValueID,
            { typesubmittime },
          );
          customFields.setAddedConfigurations(processed);
        }

        const estRem = customFields.getEstAndRem(
          fieldsResponse,
          matchingLayer.LayerId,
          'Estimated',
          'Remaining',
          'Actual',
          'Phases',
        );
        form.setEstimatedField(estRem.estimated);
        if (estRem.estimated)
        {
          validation.updateValidation('estimated', false);
        }
        form.setRemainingField(estRem.remaining);
        form.setRemainingFieldInitial(estRem.remaining);
        form.setActualField(estRem.actual);
        form.setActualFieldInitial(estRem.actual);
        form.setRemainingId(estRem.remainingId);
        form.setActualId(estRem.actualId);
      }
      catch (error)
      {
        console.error('Error loading custom fields in edit mode:', error);
      }
    }
  }, [objedit, api, validation, cascadeLoading, customFields, form]);

  // Load layers for conversation
  const loadLayers = useCallback(
    async (phaseOverride?: PhaseItem | null) =>
    {
      if (!conversation?.ConversationId) return;

      try
      {
        let layers: LayerItem[];

        const convLayers = conversation?.ConversationLayers;
        if (convLayers && convLayers.length > 0)
        {
          // Use conversation-specific layers — same source as loadLayersForConversation
          layers = convLayers.map(cl => ({
            LayerId: cl.LayerId,
            Name: cl.LayerName || (cl as any).Name || '',
            Color: cl.LayerColor || (cl as any).Color,
          }));
          cascadeLoading.setDataLayers(layers);
        }
        else
        {
          layers = await cascadeLoading.loadLayers(api, conversation.ConversationId);
          cascadeLoading.setDataLayers(layers);
        }

        // Auto-select: prefer stored default layer, fall back to first option
        const storedLayerId = parseInt(localStorage.getItem('C2_layerid') || '0', 10);
        const autoLayer: LayerItem | null =
          (storedLayerId > 0 ? layers.find(l => l.LayerId === storedLayerId) : undefined) ||
          layers[0] ||
          null;

        if (autoLayer)
        {
          handleLayerSelect(autoLayer, phaseOverride);
        }
      }
      catch (error)
      {
        console.error('Error loading layers:', error);
      }
    },
    [conversation, api, cascadeLoading],
  );

  // Load phases — returns the auto-selected phase so callers can pass it as an override
  const getPhases = useCallback(async (): Promise<PhaseItem | null> =>
  {
    if (!conversation?.ConversationId) return null;

    try
    {
      const phases = await cascadeLoading.loadPhases(api, conversation.ConversationId);
      cascadeLoading.setDataPhases(phases);

      if (phases.length > 0)
      {
        cascadeLoading.setPhaseSelected(phases[0] ?? null);
        return phases[0] ?? null;
      }
    }
    catch (error)
    {
      console.error('Error loading phases:', error);
    }
    return null;
  }, [conversation, api, cascadeLoading]);

  // Get custom field values for selected layer (calls getSubmitTimeConfigurations + getCustomFieldsByItemId)
  // taskOverride/phaseOverride/layerOverride: avoid stale closure — each corresponding setX is async,
  // so the state value is still the old one when called right after the setter.
  const getCustomFieldValues = useCallback(
    async (
      taskOverride?: TaskItem | null,
      phaseOverride?: PhaseItem | null,
      layerOverride?: LayerItem | null,
      showPhasesOverride?: boolean,
    ) =>
    {
      const layer = layerOverride !== undefined ? layerOverride : cascadeLoading.selectedId;
      if (!layer) return;

      // valForm.showPhases is stale right after updateValidation('showPhases', ...)
      const showPhases =
        showPhasesOverride !== undefined ? showPhasesOverride : validation.valForm.showPhases;

      // Determine coreTaskId, projectId and configValueId based on mode
      let coreTaskId: number | undefined;
      let projectId: number | undefined;
      let configValueId: number | undefined;

      if (typesubmittime === 1)
      {
        projectId = conversation?.ProjectId || (conversation as any)?.ProjectID;
        coreTaskId = conversation?.CoreTaskId || conversation?.CoreTaskID;
        configValueId = conversation?.ConfigValueId;
        const effectivePhase =
          phaseOverride !== undefined ? phaseOverride : cascadeLoading.phaseSelected;
        if (conversation?.HasPhases && effectivePhase)
        {
          coreTaskId = effectivePhase.CoreTaskId || effectivePhase.CoreTaskID;
        }
      }
      else if (typesubmittime === 2)
      {
        // Use taskOverride when provided (avoids reading stale state after setTaskSelected)
        const effectiveTask =
          taskOverride !== undefined ? taskOverride : cascadeLoading.taskSelected;
        coreTaskId = effectiveTask?.CoreTaskID;
        projectId = cascadeLoading.projectSelected?.ProjectId;
        configValueId = cascadeLoading.typeSelected?.ConfigValueID ?? undefined;
        const effectivePhase =
          phaseOverride !== undefined ? phaseOverride : cascadeLoading.phaseSelected;
        if (showPhases && effectivePhase)
        {
          coreTaskId = effectivePhase.CoreTaskId || effectivePhase.CoreTaskID;
        }
      }

      if (!coreTaskId || !projectId) return;

      try
      {
        setBtnSubmit(true);
        const { submitTimeService } = await import('@/services/submitTimeService');

        const [configurations, fieldsResponse] = await Promise.all([
          submitTimeService.getSubmitTimeConfigurations(),
          submitTimeService.getCustomFieldsByItemId(coreTaskId, false, projectId),
        ]);

        // Cache for subsequent layer changes (typesubmittime === 2)
        setCachedConfigurations(configurations);
        setCachedFieldsResponse(fieldsResponse);

        // Store backup for change detection
        customFields.setBackupCustomFields([...fieldsResponse] as any);

        // Process configurations filtered by ConfigValueID + LayerId
        if (configurations && configurations.length > 0)
        {
          const effectiveShowPhases = typesubmittime === 2 ? showPhases : false;
          const processed = customFields.processCustomFieldResponse(
            fieldsResponse,
            configurations,
            layer.LayerId,
            configValueId,
            {
              hasPhases: !!conversation?.HasPhases,
              showPhases: effectiveShowPhases,
              typesubmittime,
            },
          );
          customFields.setAddedConfigurations(processed);
        }
        else
        {
          customFields.setAddedConfigurations([]);
        }

        // Extract estimated, remaining, actual values and their custom field IDs
        const estRem = customFields.getEstAndRem(
          fieldsResponse,
          layer.LayerId,
          'Estimated',
          'Remaining',
          'Actual',
          'Phases',
        );

        form.setEstimatedField(estRem.estimated);
        if (estRem.estimated)
        {
          validation.updateValidation('estimated', false);
        }
        // Keep entered hours applied on top of the new phase/task's Remaining/Actual
        form.applyTimeFieldBaselines(estRem);
      }
      catch (error)
      {
        console.error('Error loading custom fields:', error);
      }
      finally
      {
        setBtnSubmit(false);
      }
    },
    [
      typesubmittime,
      conversation,
      cascadeLoading.selectedId,
      cascadeLoading.phaseSelected,
      cascadeLoading.taskSelected,
      cascadeLoading.projectSelected,
      validation.valForm.showPhases,
      customFields,
      form,
      setBtnSubmit,
    ],
  );

  // Handle layer selection
  const handleLayerSelect = useCallback(
    async (layer: LayerItem | null, phaseOverride?: PhaseItem | null) =>
    {
      cascadeLoading.setSelectedId(layer);

      if (!layer)
      {
        customFields.setAddedConfigurations(null);
        return;
      }

      // Use cache only for typesubmittime === 1 WITHOUT phases: getCustomFieldsByItemId
      // returns all layers for the conversation's coreTaskId in one call.
      // When phases are active, each phase has its own coreTaskId and may only cover
      // the auto-selected layer — cache lookup for a different layer returns nothing.
      // For typesubmittime === 2 the cache is task-scoped (one layer per task).
      // In both phase and type-2 cases, fetch fresh using the active phase/task.
      const canUseCache =
        typesubmittime === 1 &&
        !conversation?.HasPhases &&
        cachedFieldsResponse.length > 0 &&
        phaseOverride === undefined;

      if (canUseCache)
      {
        applyLayerData(layer, cachedFieldsResponse, cachedConfigurations);
      }
      else
      {
        await getCustomFieldValues(undefined, phaseOverride, layer);
      }
    },
    [
      typesubmittime,
      cascadeLoading,
      customFields,
      cachedFieldsResponse,
      cachedConfigurations,
      applyLayerData,
      getCustomFieldValues,
    ],
  );

  // Handle phase selection
  const handlePhaseSelect = useCallback(
    async (phase: PhaseItem | null) =>
    {
      cascadeLoading.setPhaseSelected(phase);

      // Standalone mode: layer is independent of the phase — keep it and reload the phase fields
      if (typesubmittime === 2)
      {
        if (!phase)
        {
          customFields.setAddedConfigurations(null);
          return;
        }
        if (cascadeLoading.selectedId)
        {
          await getCustomFieldValues(undefined, phase, undefined, true);
        }
        return;
      }

      cascadeLoading.setSelectedId(null);

      if (!phase)
      {
        customFields.setAddedConfigurations(null);
        return;
      }

      // Pass phase as override so the full chain uses the new value
      // without relying on stale state from setPhaseSelected
      await loadLayers(phase);
    },
    [typesubmittime, cascadeLoading, customFields, loadLayers, getCustomFieldValues],
  );

  // Handle project selection
  const handleProjectSelect = useCallback(
    (project: ProjectItem | null) =>
    {
      cascadeLoading.setProjectSelected(project);
      cascadeLoading.resetProjectDependents();
      validation.updateValidation('showPhases', false);
      validation.updateValidation('validPhases', false);

      if (project)
      {
        validation.updateValidation('disabledType', false);
      }
    },
    [cascadeLoading, validation],
  );

  // Handle type selection
  const handleTypeSelect = useCallback(
    (type: TypeItem | null) =>
    {
      cascadeLoading.setTypeSelected(type);
      cascadeLoading.resetTypeDependents();
      validation.updateValidation('showPhases', false);
      validation.updateValidation('validPhases', false);

      if (type)
      {
        validation.updateValidation('disabledTask', false);

        if (cascadeLoading.projectSelected && type.ConfigValueID != null)
        {
          cascadeLoading.loadTasks(
            api,
            type.ConfigValueID,
            cascadeLoading.projectSelected.ProjectId,
          );
        }
      }
    },
    [cascadeLoading, validation, api],
  );

  // Handle task selection
  const handleTaskSelect = useCallback(
    async (task: TaskItem | null) =>
    {
      cascadeLoading.setTaskSelected(task);
      cascadeLoading.setPhaseSelected(null);
      cascadeLoading.setDataPhases([]);
      validation.updateValidation('validPhases', false);

      // Tasks with phases must be posted against a phase (same as legacy TimeTrackingCtrl).
      // HasPhases comes from GetTasks as bool? — null is treated as false.
      const hasPhases = task?.HasPhases === true;
      validation.updateValidation('showPhases', hasPhases);

      if (!task) return;

      let autoPhase: PhaseItem | null = null;
      if (hasPhases)
      {
        const phases = await cascadeLoading.loadPhases(api, task.ConversationId);
        autoPhase = phases[0] ?? null;
        cascadeLoading.setPhaseSelected(autoPhase);
      }

      if (cascadeLoading.selectedId)
      {
        // Pass task/phase/showPhases directly to avoid stale closure: the setters are async so
        // state still holds the old values inside getCustomFieldValues.
        await getCustomFieldValues(task, autoPhase, undefined, hasPhases);
      }
    },
    [api, cascadeLoading, validation, getCustomFieldValues],
  );

  // Handle team selection
  const handleTeamSelect = useCallback(
    (team: Team | null) =>
    {
      cascadeLoading.setSelectedTeam(team);
    },
    [cascadeLoading],
  );

  // Overtime warning (Actual > Estimated by 60%/100%): posts a WARNING reply tagging the task's
  // "Assigned to" and "Approver" people. The backend sends the warning notifications when
  // the reply carries SubmitReplyWarningId.
  const postOvertimeWarning = useCallback(
    async (params: {
      estimated: string;
      actual: string;
      submitReplyId: number;
      conversationId: number;
      coreTaskId?: number;
      projectId?: number;
      layer: LayerItem;
      conversationPhaseId: number;
      phaseName?: string;
      socketId: string;
    }) =>
    {
      const message = getOvertimeMessage(params.estimated, params.actual);
      if (!message || !params.submitReplyId || !params.conversationId) return;

      const { submitTimeService } = await import('@/services/submitTimeService');

      // A failure here still posts the warning, only without tags (same as legacy)
      let recipients: WarningRecipient[] = [];
      if (params.coreTaskId && params.projectId)
      {
        const fieldsResponse = await submitTimeService.getCustomFieldsByItemId(
          params.coreTaskId,
          false,
          params.projectId,
        );
        recipients = getWarningRecipients(fieldsResponse, params.layer.LayerId);
      }

      const tags = getTagsInBody(buildWarningTagsHtml(recipients));
      const warningReply: ReplyRequest = {
        ReplyId: 0,
        ConversationId: params.conversationId,
        ReplyLayers: [
          {
            LayerId: params.layer.LayerId,
            LayerColor: (params.layer as any).Color,
            LayerName: params.layer.Name,
            ReplyId: 0,
          },
        ],
        Attachments: [],
        Body: buildWarningBody(params.submitReplyId, message, recipients),
        Tags: tags,
        Socketconnectionid: params.socketId,
        type: 0,
        ConversationPhaseId: params.conversationPhaseId,
        PhaseName: params.phaseName,
        possibleTags: tags.map(t => t.PersonId),
        SubmitReplyWarningId: params.submitReplyId,
      };

      const responseItem = await submitTimeService.addReply(warningReply);
      if (!responseItem) return;

      if (params.conversationPhaseId)
      {
        responseItem.ConversationPhaseId = params.conversationPhaseId;
        responseItem.PhaseName = params.phaseName;
      }

      const onWarning = onaddreplywarning || props.corgeefunctions?.onaddreplywarning;
      if (onWarning)
      {
        onWarning(params.conversationId, responseItem, true, false);
      }
      else if (onaddreplyRef.current)
      {
        responseItem.IsFromSubmitTime = true;
        onaddreplyRef.current(params.conversationId, responseItem, true, false);
      }
      conversationBus.emit('reply-added', {
        conversationId: params.conversationId,
        reply: responseItem,
      });
    },
    [onaddreplywarning, props.corgeefunctions],
  );

  // Handle form submission - full save flow matching legacy saveReply → saveTime
  const handleSubmit = useCallback(async () =>
  {
    if (isSubmittingRef.current)
    {
      return;
    }

    const isValid = validateForm();
    if (!isValid)
    {
      isSubmittingRef.current = false;
      return;
    }

    // Set flag immediately to block subsequent clicks
    isSubmittingRef.current = true;
    setBtnSubmit(true);
    setIsLoading(true);

    try
    {
      const layer = cascadeLoading.selectedId;
      if (!layer)
      {
        isSubmittingRef.current = false;
        setBtnSubmit(false);
        setIsLoading(false);
        return;
      }

      // Determine conversationId based on mode
      const conversationId =
        typesubmittime === 2
          ? cascadeLoading.taskSelected?.ConversationId || 0
          : conversation?.ConversationId || 0;

      // Determine configValueId
      const configValueId =
        typesubmittime === 2
          ? cascadeLoading.typeSelected?.ConfigValueID
          : conversation?.ConfigValueId || (conversation as any)?.selectedConfigValueId;

      // Determine projectId
      const projectId =
        typesubmittime === 2
          ? cascadeLoading.projectSelected?.ProjectId
          : conversation?.ProjectId || (conversation as any)?.ProjectID;

      // Determine coreTaskId based on mode
      let coreTaskId: number | undefined;
      if (typesubmittime === 1)
      {
        coreTaskId = conversation?.CoreTaskId || conversation?.CoreTaskID;
        if (conversation?.HasPhases && cascadeLoading.phaseSelected)
        {
          coreTaskId =
            cascadeLoading.phaseSelected.CoreTaskId || cascadeLoading.phaseSelected.CoreTaskID;
        }
      }
      else if (typesubmittime === 2)
      {
        coreTaskId = cascadeLoading.taskSelected?.CoreTaskID;
        if (validation.valForm.showPhases && cascadeLoading.phaseSelected)
        {
          coreTaskId =
            cascadeLoading.phaseSelected.CoreTaskId || cascadeLoading.phaseSelected.CoreTaskID;
        }
      }

      // Validate and save editable custom fields
      if (customFields.addedConfigurations && customFields.addedConfigurations.length > 0)
      {
        const isEmpty = (v: string | number | null | undefined) =>
          v === null || v === undefined || v === '' || v === '<p></p>';
        const invalidFields = customFields.addedConfigurations.filter(
          cf => cf.Required && cf.Editable && isEmpty(cf.Value),
        );
        if (invalidFields.length > 0)
        {
          isSubmittingRef.current = false;
          setBtnSubmit(false);
          setIsLoading(false);
          return;
        }

        const { submitTimeService } = await import('@/services/submitTimeService');
        for (const cf of customFields.addedConfigurations)
        {
          if (!cf.Editable) continue;
          if (cf.CustomFieldValueId)
          {
            // Existing record — PUT
            await submitTimeService.updateCustomField(cf as Record<string, any>);
          }
          else if (!isEmpty(cf.Value))
          {
            // New value — POST
            const saved = await submitTimeService.saveCustomField(cf as Record<string, any>);
            if (saved?.CustomFieldValueId)
            {
              customFields.handleDynamicInputChange(cf.CustomFieldId, cf.Value as string | number);
            }
          }
        }
      }

      // Build formatted reply body
      const socketId =
        socketconnectionid || (props.corgeefunctions?.getSocketConnectionId?.() ?? '');

      const existingReplyId =
        isEdit && objedit && (objedit as any).replyId > 0 ? (objedit as any).replyId : 0;

      let bodyEdited: string;
      if (isEdit)
      {
        bodyEdited =
          `<div><strong>Updated from submit time: ${formatDate(date)}</strong>` +
          `<p>${hours}</p><br/>${body.replace('&amp;', '&')}</div>`;
      }
      else
      {
        const remBase = remainingField || '0';
        const remStr = remBase.includes('.') ? remBase + '0' : remBase + '.00';
        const actBase = actualField || '0';
        const actStr = actBase.includes('.') ? actBase : actBase + '.0';
        bodyEdited =
          `<div><strong>Posted from submit time: ${formatDate(date)}</strong>` +
          `<p><br/><strong>Logged Time:</strong> ${hours}` +
          `&nbsp;&nbsp;&nbsp;<strong>Remaining:</strong> ${remStr}` +
          `&nbsp;&nbsp;&nbsp;<strong>Actual:</strong> ${actStr}</p><br/>` +
          `${body.replace('&amp;', '&')}</div>`;
      }

      // Build reply request
      const replyItem: ReplyRequest = {
        ReplyId: existingReplyId,
        ConversationId: conversationId,
        ReplyLayers: [
          {
            LayerId: layer.LayerId,
            LayerColor: (layer as any).Color,
            LayerName: layer.Name,
            ReplyId: existingReplyId,
          },
        ],
        // Only send server-expected fields. Never spread React-only fields (isUploading, etc.)
        // because undefined values serialise to null and fail C# AttachmentVM validation.
        // New files get AttachmentId: 0; existing files keep their real ID.
        Attachments: attachments
          .filter(a => !a.isUploading)
          .map(a =>
          {
            const ext =
              a.FileExtension ||
              (a.FileName?.includes('.') ? a.FileName.split('.').pop() : '') ||
              '';
            return {
              AttachmentId: a.TempFile ? 0 : a.AttachmentId || 0,
              FileName: a.FileName || '',
              FileUrl: a.FileUrl || '',
              FileSize: a.FileSize || 0,
              FileType: a.FileType || '',
              FileContentType: a.FileContentType || a.FileType || '',
              FileExtension: ext,
              Description: a.Description || '',
              TempFile: a.TempFile || '',
            };
          }),
        Body: bodyEdited,
        Tags: getTagsInBody(body),
        Socketconnectionid: socketId,
        type: 0,
        ConversationPhaseId: 0,
        possibleTags: getTagsInBody(body).map(t => t.PersonId),
      };

      // Add phase info if applicable
      if (typesubmittime === 1 && conversation?.HasPhases && cascadeLoading.phaseSelected)
      {
        replyItem.ConversationPhaseId =
          (cascadeLoading.phaseSelected as any).ConversationPhaseId || 0;
        replyItem.PhaseName = (cascadeLoading.phaseSelected as any).Title || '';
      }
      else if (
        typesubmittime === 2 &&
        validation.valForm.showPhases &&
        cascadeLoading.phaseSelected
      )
      {
        replyItem.ConversationPhaseId =
          (cascadeLoading.phaseSelected as any).ConversationPhaseId || 0;
        replyItem.PhaseName = (cascadeLoading.phaseSelected as any).Title || '';
      }

      // Captured before the edit path rewrites ConversationPhaseId for the update request
      const warningPhaseId = replyItem.ConversationPhaseId;
      const warningPhaseName = replyItem.PhaseName;

      // Build time tracking request
      // Use template literal to avoid UTC offset shift: new Date("YYYY-MM-DD") parses as
      // UTC midnight, and toISOString() would return the previous day for negative UTC offsets.
      const dateIso = `${date}T00:00:00`;

      const ttRequest: TimeTrackingItem = {
        TimeTrackingID: isEdit && objedit ? (objedit as any).TimeTrackingID : 0,
        ConversationId: conversationId,
        ConfigValueID: configValueId,
        CoreTaskID: coreTaskId,
        LayerId: layer.LayerId,
        ProjectID: projectId,
        CustomFieldId: remainingId,
        CustomFieldValue: remainingId !== undefined && remainingField === '' ? '0' : remainingField,
        CustomFieldActualId: actualId,
        CustomFieldActualValue: actualId !== undefined && actualField === '' ? '0' : actualField,
        Discriminator: 'CoreTaskCustomFieldValue',
        Date: dateIso,
        Hours: hours,
        Description: body,
        Socketconnectionid: socketId,
        warningEstimated: estimatedField,
        TeamId: cascadeLoading.selectedTeam?.TeamId ?? null,
      };

      const { submitTimeService } = await import('@/services/submitTimeService');
      let responseItem: any;

      // Split into ≤20h chunks when timeTrackingTeam, matching legacy splitTimes behavior
      const buildTtRequests = (): TimeTrackingItem[] =>
      {
        if (!timeTracking.timeTrackingTeam) return [ttRequest];
        const totalHours = parseInt(String(ttRequest.Hours ?? '0').split(':')[0] ?? '0', 10);
        const maxChunk = 20;
        if (totalHours <= maxChunk) return [ttRequest];
        const minutes = String(ttRequest.Hours ?? '0:00').split(':')[1] ?? '00';
        const requests: TimeTrackingItem[] = [];
        let remaining = totalHours;
        while (remaining > 0)
        {
          const chunk = Math.min(maxChunk, remaining);
          const isLast = remaining - chunk <= 0;
          requests.push({ ...ttRequest, Hours: `${chunk}:${isLast ? minutes : '00'}` });
          remaining -= chunk;
        }
        return requests;
      };

      if (isEdit)
      {
        if (existingReplyId > 0)
        {
          // For edit with existing reply, use mainConversationId if available
          replyItem.ConversationId = (objedit as any)?.mainConversationId || conversationId;
          replyItem.ConversationPhaseId = (objedit as any)?.mainConversationId ? conversationId : 0;
          replyItem.IsEnable = true;
          responseItem = await submitTimeService.updateReply(replyItem);
        }
        else
        {
          replyItem.IsEnable = true;
          responseItem = await submitTimeService.addReply(replyItem);
        }
        ttRequest.ReplyId = responseItem.ReplyId;
        for (const req of buildTtRequests())
        {
          await submitTimeService.updateTimeTracking({ ...req, ReplyId: responseItem.ReplyId });
        }
        await submitTimeService.setConversationsAsViewed(conversationId);

        // Add phase info to response for callback
        if (typesubmittime === 1 && conversation?.HasPhases && cascadeLoading.phaseSelected)
        {
          responseItem.ConversationPhaseId = (
            cascadeLoading.phaseSelected as any
          ).ConversationPhaseId;
          responseItem.PhaseName = (cascadeLoading.phaseSelected as any).Title;
        }

        if (onupdatereply)
        {
          onupdatereply(responseItem, responseItem.ReplyId);
        }
        conversationBus.emit('reply-updated', {
          conversationId,
          replyId: responseItem.ReplyId,
          reply: responseItem,
        });
      }
      else
      {
        responseItem = await submitTimeService.addReply(replyItem);
        ttRequest.ReplyId = responseItem.ReplyId;
        for (const req of buildTtRequests())
        {
          await submitTimeService.saveTimeTracking({ ...req, ReplyId: responseItem.ReplyId });
        }
        await submitTimeService.setConversationsAsViewed(conversationId);

        // Flag to identify reply origin
        responseItem.IsFromSubmitTime = true;

        // Add phase info to response for callback
        if (typesubmittime === 1 && conversation?.HasPhases && cascadeLoading.phaseSelected)
        {
          responseItem.ConversationPhaseId = (
            cascadeLoading.phaseSelected as any
          ).ConversationPhaseId;
          responseItem.PhaseName = (cascadeLoading.phaseSelected as any).Title;
        }

        if (onaddreplyRef.current)
        {
          onaddreplyRef.current(conversationId, responseItem, true, false);
        }
        conversationBus.emit('reply-added', {
          conversationId,
          reply: responseItem,
        });
      }

      // Non-blocking overtime warning — never affects the main save flow
      postOvertimeWarning({
        estimated: estimatedField,
        actual: actualField,
        submitReplyId: responseItem.ReplyId,
        conversationId,
        coreTaskId,
        projectId,
        layer,
        conversationPhaseId: warningPhaseId,
        phaseName: warningPhaseName,
        socketId,
      }).catch(error => console.error('Error posting overtime warning:', error));

      // Callbacks — pass conversation so the parent can refresh details + custom fields
      if (onSubmitTimeRef.current)
      {
        onSubmitTimeRef.current(conversation);
      }
      if (onSave)
      {
        onSave();
      }

      // Re-evaluate blocking: the saved entry may satisfy yesterday's 8-hour requirement
      validateYesterdayMetrics();

      // "Save and New": keep the modal open and leave it ready for another entry
      if (saveAndNewRef.current && !isEdit)
      {
        setCachedFieldsResponse([]);
        setCachedConfigurations([]);
        resetForm(false);
        // Remount the child fields so the rich text editor and inputs are cleared
        setCustomKey(Math.floor(Math.random() * 1000000));
        // Triggers initializeModal on the next render — same flow as reopening the modal
        setReopenNonce(prev => prev + 1);
      }
      else
      {
        // Reset form and close
        resetForm(false);
        handleClose();
      }
    }
    catch (error)
    {
      console.error('Error submitting time:', error);
      alert('Error submitting time. Please try again.');
    }
    finally
    {
      isSubmittingRef.current = false;
      setBtnSubmit(false);
      setIsLoading(false);
    }
  }, [
    validateForm,
    body,
    attachments,
    date,
    hours,
    estimatedField,
    remainingField,
    actualField,
    remainingId,
    actualId,
    conversation,
    isEdit,
    objedit,
    socketconnectionid,
    typesubmittime,
    cascadeLoading,
    validation,
    customFields,
    form,
    onupdatereply,
    onSave,
    resetForm,
    handleClose,
    postOvertimeWarning,
    props.corgeefunctions,
  ]);

  // Handle delete button click
  const handleDelete = useCallback(() =>
  {
    setIsOpenQuestionDelete(true);
  }, [setIsOpenQuestionDelete]);

  // Confirm delete - calls deleteReply + deleteAndUpdate
  const confirmDelete = useCallback(async () =>
  {
    if (!objedit) return;

    // Close the confirm dialog immediately — the main modal shows a "Deleting…" state while processing
    setIsOpenQuestionDelete(false);
    setIsDeleting(true);
    try
    {
      const socketId =
        socketconnectionid || (props.corgeefunctions?.getSocketConnectionId?.() ?? '');

      const deleteRequest: DeleteAndUpdateRequest = {
        TimeTrackingID: (objedit as any).TimeTrackingID,
        CoreTaskID: objedit.CoreTaskID,
        ProjectID: cascadeLoading.projectSelected?.ProjectId || objedit.ProjectID,
        Date: `${objedit.date}T00:00:00`,
        Socketconnectionid: socketId,
        Description: objedit.Description,
      };

      // Helper: HH:MM → decimal hours (matches legacy sumTimeToFloat/subTimeToFloat input)
      const hhmmToDecimal = (h: string): number =>
      {
        const [hh, mm] = (h || '0:00').split(':').map(Number);
        return (hh || 0) + (mm || 0) / 60;
      };

      // Restore remaining on delete: hoursInitial + remainingFieldInitial (decimal)
      // Matches legacy: sumTimeToFloat(hoursInitial, remainingInitial) → decimal string
      if (remainingId !== undefined)
      {
        const hoursInitDec = hhmmToDecimal(form.hoursInitial || form.hours || '0:00');
        const remInit = parseFloat(form.remainingFieldInitial || '0');
        deleteRequest.CustomFieldId = remainingId;
        deleteRequest.CustomFieldValue = parseFloat((hoursInitDec + remInit).toFixed(2)).toString();
      }

      // Restore actual on delete: actualFieldInitial - hoursInitial (decimal)
      // Matches legacy: subTimeToFloat(hoursInitial, actualFieldInitial)
      if (actualId !== undefined)
      {
        const hoursInitDec = hhmmToDecimal(form.hoursInitial || form.hours || '0:00');
        const actInit = parseFloat(form.actualFieldInitial || '0');
        deleteRequest.CustomFieldActualId = actualId;
        deleteRequest.CustomFieldActualValue = Math.max(
          0,
          parseFloat((actInit - hoursInitDec).toFixed(2)),
        ).toString();
        deleteRequest.Discriminator = 'CoreTaskCustomFieldValue';
      }

      const { submitTimeService } = await import('@/services/submitTimeService');

      // Delete reply if it exists
      const replyId = (objedit as any).replyId;
      if (replyId && replyId > 0)
      {
        const deleteReplyReq: DeleteReplyRequest = {
          ReplyId: replyId,
          ConversationId: (objedit as any).mainConversationId || objedit.ConversationId,
          Body: '',
          SocketConnectionId: socketId,
          ConversationPhaseId: (objedit as any).mainConversationId ? objedit.ConversationId : 0,
        };
        await submitTimeService.deleteReply(deleteReplyReq);
      }

      // Delete and update the time tracking entry
      await submitTimeService.deleteAndUpdate(deleteRequest);

      // Callbacks
      if (onSubmitTime)
      {
        onSubmitTime();
      }

      // Re-evaluate blocking: deleting an entry may drop hours below 8
      validateYesterdayMetrics();

      handleClose();
    }
    catch (error)
    {
      console.error('Error deleting time entry:', error);
      alert('Error deleting time entry. Please try again.');
    }
    finally
    {
      setIsDeleting(false);
    }
  }, [
    objedit,
    socketconnectionid,
    cascadeLoading.projectSelected,
    remainingId,
    actualId,
    remainingField,
    actualField,
    timeCalculations,
    form.hours,
    onSubmitTime,
    handleClose,
    props.corgeefunctions,
  ]);

  return (
    <>
      <SubmitTimeModal
        show={form.openmodal}
        title={isEdit ? 'Time Tracking' : title}
        onClose={handleClose}
        onSave={handleSubmit}
        onDelete={handleDelete}
        isEdit={isEdit}
        isSaving={isLoading || isDeleting}
        isProcessing={isLoading || isDeleting}
        submitLabel={isDeleting ? 'Deleting' : 'Submit'}
        canDelete={isEdit}
        isInitializing={isInitializing}
        onSaveAndNewChange={checked =>
        {
          saveAndNewRef.current = checked;
        }}
      >
        <div className="submit-time-content">
          {/* Selectors Section */}
          <SelectorsSection
            dataLayers={cascadeLoading.dataLayers}
            dataPhases={cascadeLoading.dataPhases}
            dataProjects={cascadeLoading.dataProjects}
            dataType={cascadeLoading.dataType}
            dataTasks={cascadeLoading.dataTasks}
            defaultTeams={cascadeLoading.defaultTeams}
            selectedId={cascadeLoading.selectedId}
            phaseSelected={cascadeLoading.phaseSelected}
            projectSelected={cascadeLoading.projectSelected}
            typeSelected={cascadeLoading.typeSelected}
            taskSelected={cascadeLoading.taskSelected}
            selectedTeam={cascadeLoading.selectedTeam}
            valForm={validation.valForm}
            onLayerChange={handleLayerSelect}
            onPhaseChange={handlePhaseSelect}
            onProjectChange={handleProjectSelect}
            onTypeChange={handleTypeSelect}
            onTaskChange={handleTaskSelect}
            onTeamChange={handleTeamSelect}
            isLoadingLayers={cascadeLoading.isLoadingLayers}
            isLoadingPhases={cascadeLoading.isLoadingPhases}
            isLoadingProjects={cascadeLoading.isLoadingProjects}
            isLoadingTypes={cascadeLoading.isLoadingTypes}
            isLoadingTasks={cascadeLoading.isLoadingTasks}
            isLoadingTeams={cascadeLoading.isLoadingTeams}
            showTeams={timeTracking.timeTrackingTeam}
            customKey={customKey}
          />

          {/* Time Input Fields */}
          <TimeInputFields
            hours={hours}
            date={date}
            estimatedField={estimatedField}
            remainingField={remainingField}
            actualField={actualField}
            description={description}
            valForm={validation.valForm}
            onHoursChange={handleHoursChange}
            onHoursBlur={handleHoursBlur}
            hoursFormat={hoursFormat}
            onDateChange={handleDateChange}
            onEstimatedChange={handleEstimatedChange}
            onRemainingChange={handleRemainingChange}
            onDescriptionChange={handleDescriptionChange}
            showEstimated={
              !!estimatedField || !!customFields.addedConfigurations || validation.valForm.estimated
            }
            showRemaining={remainingId !== undefined || !!remainingField}
            showActual={actualId !== undefined || !!actualField}
            showDescription={false}
            timeTrackingTeam={timeTracking.timeTrackingTeam}
            customKey={customKey}
          />

          {/* Dynamic Custom Fields */}
          <DynamicCustomFields
            customFields={customFields.addedConfigurations}
            canEdit={typesubmittime === 2 || !isEdit}
            apiConfig={{
              apiUrl: api?.apiUrl,
              token: api?.token,
            }}
            disableAutoSave={false}
            configValueId={
              typesubmittime === 1
                ? conversation?.ConfigValueId
                : (cascadeLoading.typeSelected?.ConfigValueID ?? undefined)
            }
            layerId={cascadeLoading.selectedId?.LayerId}
            projectId={
              typesubmittime === 1
                ? conversation?.ProjectId || (conversation as any)?.ProjectID
                : cascadeLoading.projectSelected?.ProjectId
            }
            handleDynamicInputChange={customFields.handleDynamicInputChange}
            updateCustomFields={customFields.updateCustomField as any}
          />

          {/* Reply Editor */}
          <ReplyEditor
            body={body}
            attachments={attachments}
            canEdit={true}
            onBodyChange={handleBodyChange}
            onAttachmentAdd={handleAttachmentAdd}
            onAttachmentRemove={handleAttachmentRemove}
            validationError={validation.valForm.description}
            customKey={customKey}
            conversationId={conversation?.ConversationId}
            projectId={conversation?.ProjectId}
            LayerItem={cascadeLoading.selectedId ?? undefined}
            layers={cascadeLoading.dataLayers}
            apiConfig={{ apiUrl: api?.url, token: api?.token }}
            configValueId={
              typesubmittime === 1
                ? conversation?.ConfigValueId
                : (cascadeLoading.typeSelected?.ConfigValueID ?? undefined)
            }
          />
        </div>
      </SubmitTimeModal>

      {/* Delete confirmation dialog */}
      {isOpenQuestionDelete && (
        <SubmitTimeModal
          show={isOpenQuestionDelete}
          title="Confirm Delete"
          onClose={() => setIsOpenQuestionDelete(false)}
          onSave={confirmDelete}
          isEdit={false}
          isSaving={isLoading}
          submitLabel="Yes"
          showSaveAndNew={false}
        >
          <p>Are you sure you want to delete this time entry?</p>
        </SubmitTimeModal>
      )}
    </>
  );
};

export default SubmitTime;
